// src/modules/agents/pages/AgentList.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Input, Space, Tag, Typography, message, Popconfirm } from 'antd';
import { PlusOutlined, SearchOutlined, TeamOutlined, DollarOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { fetchAgents, deleteAgent } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';
import dayjs from 'dayjs';

const { Title } = Typography;

const AgentList = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');

  const loadAgents = useCallback(async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const res = await fetchAgents({ search, page, limit });
      setAgents(res.data || []);
      setPagination((prev) => ({
        ...prev,
        current: res.meta?.page || page,
        total: res.meta?.total || 0,
      }));
    } catch (err) {
      message.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteAgent(id);
      message.success('Agent and related data deleted');
      loadAgents();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to delete agent');
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'Agent ID',
      dataIndex: 'agentId',
      render: (id, record) => (
        <a onClick={() => navigate(`/agents/${record._id}`)}>{id}</a>
      ),
    },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Phone', dataIndex: 'phone' },
    {
      title: 'Default Commission %',
      dataIndex: 'defaultCommissionPercent',
      render: (v) => `${v}%`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s) => (
        <Tag color={s === 'ACTIVE' ? 'green' : 'red'}>{s}</Tag>
      ),
    },
    {
      title: 'Total Earnings',
      dataIndex: 'totalEarnings',
      render: (v) => `₹${(v || 0).toLocaleString('en-IN')}`,
    },
    {
      title: 'Pending',
      dataIndex: 'totalPending',
      render: (v) => `₹${(v || 0).toLocaleString('en-IN')}`,
    },
    {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      render: (v) => v || '-',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/agents/${record._id}`)}
            size="small"
          >
            View
          </Button>
          {can('agents', 'edit') && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/agents/edit/${record._id}`)}
              size="small"
            >
              Edit
            </Button>
          )}
          {can('agents', 'delete') && (
            <Popconfirm
              title="Delete this agent?"
              description="All commissions linked to this agent will also be deleted."
              okText="Delete"
              okButtonProps={{ danger: true, loading: deletingId === record._id }}
              onConfirm={() => handleDelete(record._id)}
            >
              <Button danger size="small" icon={<DeleteOutlined />} loading={deletingId === record._id}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <TeamOutlined /> Agents
          </Title>
          <Space>
            <Input
              placeholder="Search agents..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => loadAgents()}
              style={{ width: 250 }}
              allowClear
            />
            <Button
              icon={<DollarOutlined />}
              onClick={() => navigate('/agents/settlements')}
              style={{ background: '#faad14', borderColor: '#faad14', color: '#fff' }}
            >
              Settlements
            </Button>
            {can('agents', 'create') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/agents/create')}>
                Add Agent
              </Button>
            )}
          </Space>
        </div>

        <Table
          dataSource={agents}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} agents`,
            onChange: (page, pageSize) => loadAgents(page, pageSize),
          }}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default AgentList;
